import {useDataQuery, useRpcQuery, useViewQuery} from '@tnc/shared';
import {Map, Marker} from '@vis.gl/react-google-maps';

import {profileDetailsDataItemT} from '@/lib/types/form.types';
import {positionT} from '@/lib/types/position.types';

import {useProduct} from '@/pages/(shared)/products/detail/providers/productProvider';

import {useAuth} from '@/providers/components/authProvider';

import {
  ProductDetailsMediaGalleryComponent,
  ProfileCardComponent,
  ProfileDetailsTabListComponent,
  profileCardSectionT,
} from '@/components';
import Page from '@/components/Page';
import {ElasticDataTable} from '@/components/elasticDatatable';

import ProductDetailsPositionLog from './productDetailsPositionLog';

function ProductDetails() {
  const {product} = useProduct();
  const {isAdmin} = useAuth();

  // const [distributorInventory = [], setDistributorInventory] = useState<itemT[]>([]);
  // const [retailerInventory = [], setRetailerInventory] = useState<itemT[]>([]);

  const {data: {data: distributorInventory} = {data: []}} = useDataQuery({
    table: 'Item',
    filters: [
      {column: 'fk_product_id', operator: 'eq', value: product?.product_id},
      {column: 'item_inventory_type', operator: 'eq', value: 'distributor'},
    ],
    columns: ['*'],
    enabled: !!product?.product_id,
  });

  const {data: {data: retailerInventory} = {data: []}} = useDataQuery({
    table: 'Item',
    filters: [
      {column: 'fk_product_id', operator: 'eq', value: product?.product_id},
      {column: 'item_inventory_type', operator: 'eq', value: 'retailer'},
    ],
    columns: ['*'],
    enabled: !!product?.product_id,
  });

  const {data: {data: positions} = {data: []}} = useDataQuery({
    table: 'Position',
    filters: [{column: 'fk_product_id', operator: 'eq', value: product?.product_id}],
    columns: ['*'],
    enabled: !!product?.product_id,
  });

  const {data: marketInfoData} = useViewQuery({
    view: 'product_market_info',
    filters: [{column: 'product_id', operator: 'eq', value: product?.product_id ?? null}],
    enabled: !!product?.product_id,
  });

  const {data: mapData} = useRpcQuery({
    functionName: 'get_product_details_map_data',
    functionArgs: {
      product_id: product?.product_id || 0,
    },
    enabled: !!product?.product_id,
  });

  const distributorMarkers = mapData?.data[0]?.distributor_locations;
  const retailerMarkers = mapData?.data[0]?.retailer_locations;

  const headerInfo = {
    subHeader: `${product?.product_category} - ${product?.product_subcategory}`,
    stats: [
      {number: retailerInventory.length, title: 'Total Retailers'},
      {number: positions.length, title: 'Positions Made'},
    ],
  };

  const sections: profileCardSectionT[] = [
    {
      title: 'Product Information',
      items: [
        {header: 'Brand', content: product?.Brand.brand_name},
        {header: 'Tags', content: String(product?.product_tags)},
        {header: 'Name', content: String(product?.product_name)},
      ],
    },
    {
      title: 'Brand Information',
      items: [
        {
          header: 'Brand',
          content: product?.Brand.brand_name,
          avatar: product?.Brand.brand_logo_url || '/images/logo/logo.png',
        },
        {header: 'Brand Description', content: product?.Brand.brand_description},
      ],
    },
    {
      title: 'Market Information',
      items: [
        {header: 'Similar Market Items', content: marketInfoData?.data[0].similar_market_items},
        {header: 'Similar Distribution Inventory', content: marketInfoData?.data[0].similar_distribution_inventory},
        {header: 'Similar Retailer Inventory', content: marketInfoData?.data[0].similar_retailer_inventory},
      ],
    },
  ];

  const tabs = [
    {
      id: 'positionData',
      title: 'Position Log',
      icon: 'ri-list-check-2',
      content: <ProductDetailsPositionLog />,
    },
    {
      id: 'productData',
      title: 'Product Data',
      icon: 'ri-store-2-line',
      content: (
        <div>
          <ElasticDataTable
            index="tnc.weedmaps.products-0000000000"
            columns={[
              {
                field: 'name',
                header: 'Product Name',
                render: row => {
                  return <div>{row.name}</div>;
                },
              },
              {
                field: 'price_status',
                header: 'Product Name',
                render: row => {
                  return <div>{row.price_stats.min}</div>;
                },
              },
            ]}
            filters={[
              {
                field: 'name',
                label: 'Product Name',
                type: 'multiple-select',
                placeholder: 'Search products...',
                useTypeahead: true,
                minChars: 1,
                aggregationField: 'name.keyword',
                ...(product?.Weedmap_Product?.name || product?.product_name
                  ? {defaultValue: [product?.Weedmap_Product?.name || product?.product_name]}
                  : {}),
              },
            ]}
            enabled={!!product}
          />
        </div>
      ),
    },
    {
      id: 'gallery',
      title: 'Media Gallery',
      icon: 'ri-image-line',
      content: product?.product_id && (
        <ProductDetailsMediaGalleryComponent product={product} productId={product.product_id} />
      ),
    },
  ];

  const myDataItems: profileDetailsDataItemT[] = [
    {
      type: 'generic',
      title: 'Distributor Inventory',
      data: distributorInventory.map(inventory => ({
        item: inventory,
        texts: [
          {text1: 'Date:', text2: new Date(inventory.date_created).toLocaleDateString()},
          {text1: 'Inventory Type:', text2: `${inventory.item_inventory_type}`},
          {text1: 'Taxes:', text2: `$${inventory.dutchie_taxes_total}`},
        ],
      })),
      className: `${isAdmin ? 'col-span-12 xl:col-span-4' : 'col-span-12 xl:col-span-6'}`,
    },
    {
      type: 'generic',
      title: 'Retailer Inventory',
      data: retailerInventory.map(inventory => ({
        item: inventory,
        texts: [
          {text1: 'Date:', text2: new Date(inventory.date_created).toLocaleDateString()},
          {text1: 'Inventory Type:', text2: `${inventory.item_inventory_type}`},
          {text1: 'Taxes:', text2: `$${inventory.dutchie_taxes_total}`},
        ],
      })),
      className: `${isAdmin ? 'col-span-12 xl:col-span-4' : 'col-span-12 xl:col-span-6'}`,
    },
  ];

  // if admin, show pending positions
  if (isAdmin) {
    myDataItems.push({
      type: 'generic',
      title: 'Pending Positions',
      data: positions.map((position: positionT) => ({
        item: position,
        texts: [
          {text1: 'Position Date:', text2: new Date(position.date_created).toLocaleDateString()},
          {text1: 'Position Status:', text2: position.position_status},
        ],
      })),
    });
  }

  return (
    <Page currentPage="Products">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          avatar={product?.product_image_url || '/images/logo/logo.png'}
          name={product?.product_name || ''}
          headerInfo={headerInfo}
          sections={sections}
        />
        <ProfileDetailsTabListComponent
          tabs={tabs}
          dataItems={myDataItems}
          className="col-span-12 xl:col-span-12 xxl:col-span-8"
        >
          <div className="rounded-lg bg-slate-500 p-4 shadow-md">
            <Map
              style={{width: '100%', height: '400px'}}
              defaultCenter={{lat: 34.5, lng: -118.2911198}}
              defaultZoom={10}
              gestureHandling={'greedy'}
              disableDefaultUI={true}
            >
              {/* Distributor Markers */}
              {distributorMarkers &&
                // @ts-expect-error - fix later
                distributorMarkers.map((location, index) => (
                  <Marker
                    key={`distributor-${index}`}
                    position={{
                      lat: Number(location.geo_points[0]),
                      lng: Number(location.geo_points[1]),
                    }}
                    icon={{
                      url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png', // Blue icon for distributors
                    }}
                    title={`Distributor: ${location.distributor_name}`}
                  />
                ))}

              {/* Retailer Markers */}
              {retailerMarkers &&
                // @ts-expect-error - fix later
                retailerMarkers.map((location, index) => (
                  <Marker
                    key={`retailer-${index}`}
                    position={{
                      lat: Number(location.geo_points[0]),
                      lng: Number(location.geo_points[1]),
                    }}
                    icon={{
                      url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png', // Red icon for retailers
                    }}
                    title={`Retailer: ${location.retailer_name}`}
                  />
                ))}
            </Map>
          </div>
        </ProfileDetailsTabListComponent>
      </div>
    </Page>
  );
}

export default ProductDetails;
